import { handleApiCall } from '@/apiClient'
import { JSON_HEADERS } from '@/services/headers'
import { serializeByKey } from '@/utils/serializeByKey'
import type { ApiSuccessResponse, InvoiceData, MedicalAnswerValue } from '@/types'

interface ThreadReplyPayload {
  thread_id: number
  content: string
  attachments?: Array<{ filename: string; file_data: string; description?: string }>
}

export interface NewThreadPayload {
  department: string
  subject: string
  content: string
  attachments?: Array<{ filename: string; file_data: string; description?: string }>
}

interface CancelConsultPayload {
  consult_id: number
}

interface GetSlotsPayload {
  startDateTime: string
  endDateTime: string
  aggregate: boolean
  category: number
  existing_is_error?: boolean
}

export interface ReviewPayload {
  score?: number
  motivation?: string
  anonymous?: boolean
}

export interface AppUsagePayload {
  displayMode: 'standalone' | 'browser'
  // Device and IP komen uit het verzoek zelf, niet uit deze payload.
  buildId?: string
  event?: 'prompt_seen' | 'install_accepted'
}

export interface RebateRequestPayload {
  attachments: Array<{ filename: string; data: string; description?: string }>
}

const PatientService = {
  // Cheap auth-state probe used to bootstrap `isAuthenticated` after a refresh:
  // the HttpOnly cookie is not readable from JS, so we ask the backend instead.
  async whoami(): Promise<ApiSuccessResponse> {
    return handleApiCall('/entities/whoami', { method: 'GET' })
  },
  // Hoe het portaal geopend is (app of browser). Stil en zonder gevolgen voor
  // de patient: de aanroeper vangt fouten en doet er niets mee.
  async reportAppUsage(payload: AppUsagePayload): Promise<ApiSuccessResponse> {
    return handleApiCall('/patientPortal/appUsage', {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify(payload),
    })
  },
  async getInfo(): Promise<ApiSuccessResponse> {
    return handleApiCall('/patientPortal/getInfo', {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify({
        modules: [
          'user',
          'name',
          'personal',
          'address',
          'pharmacy',
          'identificationInfo',
          'identificationSession',
          'insuranceInfo',
          'questionnaires',
          'referrals',
          'nextConsult',
          'documents',
          'consults',
          'internal_message_threads',
          'medicalOverview',
          'medication',
        ],
      }),
    })
  },
  async startIdentification(): Promise<ApiSuccessResponse> {
    return handleApiCall('/patientPortal/identification/startIdentification', {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify({ redirectUrl: `${window.location.origin}/dashboard` }),
    })
  },
  async setBSN(bsn: string): Promise<ApiSuccessResponse> {
    return handleApiCall('/patientPortal/identification/setBSN', {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify({ bsn: bsn }),
    })
  },
  async getQuestionnaireList(): Promise<ApiSuccessResponse> {
    return handleApiCall('/patientPortal/questionnaire/list', {
      method: 'GET',
      headers: JSON_HEADERS,
    })
  },
  async getQuestions(qr_id: number): Promise<ApiSuccessResponse> {
    return handleApiCall(`/patientPortal/questionnaire/${qr_id}/questions`, {
      method: 'GET',
      headers: JSON_HEADERS,
    })
  },
  async setAnswer(qr_id: number, q_id: number, answer: MedicalAnswerValue): Promise<ApiSuccessResponse> {
    return serializeByKey(`${qr_id}:${q_id}`, () =>
      handleApiCall(`/patientPortal/questionnaire/${qr_id}/set/${q_id}`, {
        method: 'POST',
        headers: JSON_HEADERS,
        body: JSON.stringify({ answer }),
      })
    )
  },
  async submitQuestionnaire(qr_id: number): Promise<ApiSuccessResponse> {
    return handleApiCall(`/patientPortal/questionnaire/${qr_id}/submit`, {
      method: 'POST',
      headers: JSON_HEADERS,
    })
  },
  async getDocument(id: number | string) {
    return handleApiCall('/patientPortal/document/get', {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify({ documentId: id, data: true }),
    })
  },
  async getSettings(): Promise<ApiSuccessResponse> {
    return handleApiCall('/patientPortal/settings', {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify({}),
    })
  },
  async setSetting(key: string, value: unknown): Promise<ApiSuccessResponse> {
    return handleApiCall(`/patientPortal/settings/${key}`, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify({ value: value }),
    })
  },
  async getEmployees(): Promise<ApiSuccessResponse> {
    return handleApiCall('/patientPortal/getEmployees', {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify({}),
    })
  },
  async getMessageThread(id: number): Promise<ApiSuccessResponse> {
    return handleApiCall('/patientPortal/thread/get', {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify({ thread_id: id, mark_messages_as_read: true }),
    })
  },
  async threadReply(payload: ThreadReplyPayload): Promise<ApiSuccessResponse> {
    return handleApiCall('/patientPortal/thread/reply', {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify(payload),
    })
  },
  async getSlots(payload: GetSlotsPayload): Promise<ApiSuccessResponse> {
    return handleApiCall('/patientPortal/getSlots', {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify(payload),
    })
  },
  async addConsult(startDateTime: string, availabilityId: number, consultTypeId: number): Promise<ApiSuccessResponse> {
    return handleApiCall('/patientPortal/addConsult', {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify({
        startDateTime,
        availabilityId,
        consultTypeId,
      }),
    })
  },
  async moveConsult(
    cancel_consult_id: number,
    startDateTime: string,
    availabilityId: number,
    consultTypeId: number
  ): Promise<ApiSuccessResponse> {
    return handleApiCall('/patientPortal/moveConsult', {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify({
        cancel_consult_id,
        startDateTime,
        availabilityId,
        consultTypeId,
      }),
    })
  },
  async newThread(payload: NewThreadPayload): Promise<ApiSuccessResponse> {
    return handleApiCall('/patientPortal/thread/new', {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify(payload),
    })
  },
  async cancelConsult(payload: CancelConsultPayload): Promise<ApiSuccessResponse> {
    return handleApiCall('/patientPortal/cancelConsult', {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify(payload),
    })
  },
  async consultReview(uuid: string, payload: ReviewPayload): Promise<ApiSuccessResponse> {
    return handleApiCall(`/patientPortal/review/consult/${uuid}/add`, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify(payload),
    })
  },
  async validateReview(uuid: string, payload?: ReviewPayload): Promise<ApiSuccessResponse> {
    return handleApiCall(`/patientPortal/review/consult/${uuid}/validate`, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify(payload),
    })
  },
  async getSubtrajectList(): Promise<ApiSuccessResponse> {
    return handleApiCall('/patientPortal/subtraject/list', {
      method: 'GET',
      headers: JSON_HEADERS,
    })
  },
  async getInvoiceList(): Promise<ApiSuccessResponse> {
    return handleApiCall('/patientPortal/invoice/list', {
      method: 'GET',
      headers: JSON_HEADERS,
    })
  },
  async getPaymentUrl(invoice_id: string | undefined): Promise<ApiSuccessResponse> {
    return handleApiCall(`/patientPortal/invoice/${invoice_id}/payment_link`, {
      method: 'GET',
      headers: JSON_HEADERS,
    })
  },
  async getInvoice(invoice_id: string) {
    return handleApiCall<InvoiceData>(`/patientPortal/invoice/${invoice_id}`, {
      method: 'GET',
      headers: JSON_HEADERS,
    })
  },
  async postRebateRequest(invoice_id: string, payload: RebateRequestPayload): Promise<ApiSuccessResponse> {
    return handleApiCall(`/patientPortal/invoice/${invoice_id}/rebate/request`, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify(payload),
    })
  },
  async withdrawRebateRequest(invoice_id: string | undefined, rebate_id: number): Promise<ApiSuccessResponse> {
    return handleApiCall(`/patientPortal/invoice/${invoice_id}/rebate/${rebate_id}/withdraw`, {
      method: 'POST',
      headers: JSON_HEADERS,
    })
  },
  async getProfilePhoto(): Promise<ApiSuccessResponse> {
    return handleApiCall('/patientPortal/profilePhoto', {
      method: 'GET',
      headers: JSON_HEADERS,
    })
  },
  async uploadProfilePhoto(filename: string, fileData: string): Promise<ApiSuccessResponse> {
    return handleApiCall('/patientPortal/profilePhoto/put', {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify({ filename, data: fileData }),
    })
  },
  async deleteProfilePhoto(): Promise<ApiSuccessResponse> {
    return handleApiCall('/patientPortal/profilePhoto/delete', {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify({}),
    })
  },
}

export default PatientService
